import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#8B5CF6',
      light: '#A78BFA',
      dark: '#7C3AED',
    },
    secondary: {
      main: '#06B6D4',
      light: '#22D3EE',
      dark: '#0891B2',
    },
    background: {
      default: '#04040A',
      paper: '#0A0A16',
    },
    success: { main: '#10B981', light: '#34D399' },
    error:   { main: '#EF4444', light: '#F87171' },
    warning: { main: '#F59E0B', light: '#FCD34D' },
    text: {
      primary:   '#F0F4FF',
      secondary: '#94A3B8',
    },
    divider: 'rgba(139,92,246,0.1)',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 800, fontFamily: '"Space Grotesk", sans-serif' },
    h2: { fontWeight: 700, fontFamily: '"Space Grotesk", sans-serif' },
    h3: { fontWeight: 700, fontFamily: '"Space Grotesk", sans-serif' },
    h4: { fontWeight: 700, fontFamily: '"Space Grotesk", sans-serif' },
    h5: { fontWeight: 600, fontFamily: '"Space Grotesk", sans-serif' },
    h6: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: { borderRadius: 14 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '10px 24px',
          boxShadow: 'none',
          fontWeight: 700,
          transition: 'all 0.25s cubic-bezier(0.22,1,0.36,1)',
          '&:hover': { boxShadow: 'none', transform: 'translateY(-2px)' },
          '&:active': { transform: 'scale(0.97)' },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)',
          boxShadow: '0 4px 20px rgba(124,58,237,0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
            boxShadow: '0 6px 28px rgba(139,92,246,0.45)',
          },
        },
        outlined: {
          borderColor: 'rgba(139,92,246,0.3)',
          color: '#A78BFA',
          '&:hover': {
            borderColor: '#8B5CF6',
            background: 'rgba(139,92,246,0.08)',
            boxShadow: '0 0 16px rgba(139,92,246,0.2)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: 'rgba(10,10,20,0.84)',
          border: '1px solid rgba(139,92,246,0.13)',
          backdropFilter: 'blur(24px) saturate(160%)',
          borderRadius: 20,
          boxShadow: '0 2px 4px rgba(0,0,0,0.5), 0 8px 24px rgba(0,0,0,0.35)',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
        },
      },
    },
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    MuiChip: {
      styleOverrides: { root: { borderRadius: 10, fontWeight: 600, fontSize: 12 } },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 6, height: 5, backgroundColor: 'rgba(139,92,246,0.12)' },
        bar: {
          background: 'linear-gradient(90deg, #7C3AED, #06B6D4)',
          boxShadow: '0 0 10px rgba(139,92,246,0.5)',
          borderRadius: 6,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 12, backdropFilter: 'blur(10px)', border: '1px solid' },
        standardError:   { backgroundColor: 'rgba(239,68,68,0.08)',   borderColor: 'rgba(239,68,68,0.3)',   color: '#FCA5A5' },
        standardSuccess: { backgroundColor: 'rgba(16,185,129,0.08)',  borderColor: 'rgba(16,185,129,0.3)',  color: '#6EE7B7' },
        standardInfo:    { backgroundColor: 'rgba(6,182,212,0.08)',   borderColor: 'rgba(6,182,212,0.3)',   color: '#67E8F9' },
        standardWarning: { backgroundColor: 'rgba(245,158,11,0.08)',  borderColor: 'rgba(245,158,11,0.3)',  color: '#FCD34D' },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(10,10,20,0.95)',
          border: '1px solid rgba(139,92,246,0.2)',
          borderRadius: 8,
          fontSize: 12,
          backdropFilter: 'blur(12px)',
        },
        arrow: { color: 'rgba(10,10,20,0.95)' },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#8B5CF6',
            boxShadow: '0 0 0 3px rgba(139,92,246,0.18)',
          },
        },
        notchedOutline: { borderColor: 'rgba(139,92,246,0.2)' },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease',
          '&:hover': { transform: 'scale(1.08)' },
          '&:active': { transform: 'scale(0.95)' },
        },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: 'rgba(139,92,246,0.1)' } },
    },
    MuiSnackbar: {
      defaultProps: { anchorOrigin: { vertical: 'bottom', horizontal: 'right' } },
    },
  },
});

export default theme;
